import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, CalendarDays, Users, Users2, Megaphone, Building2,
  ClipboardCheck, BarChart3, PieChart, Inbox, Coins, LogOut, Menu, X, Bell, ShieldCheck,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LangSwitcher } from "./LangSwitcher";
import { usePrivateSeo } from "../lib/useSeo";
import { api } from "../lib/api";

const links = [
  { to: "/staff", icon: LayoutDashboard, key: "nav.dashboard", roles: ["super_admin", "admin", "team_leader"], testId: "staff-nav-dashboard" },
  { to: "/staff/review", icon: ClipboardCheck, key: "nav.review", roles: ["super_admin", "admin", "team_leader"], testId: "staff-nav-review" },
  { to: "/staff/events", icon: CalendarDays, key: "nav.events", roles: ["super_admin", "admin", "team_leader"], testId: "staff-nav-events" },
  { to: "/staff/analytics", icon: PieChart, key: "nav.analytics", roles: ["super_admin", "admin", "team_leader"], testId: "staff-nav-analytics" },
  { to: "/staff/reports", icon: BarChart3, key: "nav.reports", roles: ["super_admin", "admin", "team_leader"], testId: "staff-nav-reports" },
  { to: "/staff/lead-outbox", icon: Inbox, key: "nav.outbox", roles: ["super_admin", "admin", "team_leader"], testId: "staff-nav-outbox" },
  { to: "/staff/leads", icon: Building2, key: "nav.leads", roles: ["super_admin", "admin", "team_leader"], testId: "staff-nav-leads" },
  { to: "/staff/users", icon: Users, key: "nav.users", roles: ["super_admin", "admin"], testId: "staff-nav-users" },
  { to: "/staff/teams", icon: Users2, key: "nav.teams", roles: ["super_admin", "admin"], testId: "staff-nav-teams" },
  { to: "/staff/campaigns", icon: Megaphone, key: "nav.campaigns", roles: ["super_admin", "admin"], testId: "staff-nav-campaigns" },
  { to: "/staff/partners", icon: Building2, key: "nav.partners", roles: ["super_admin", "admin"], testId: "staff-nav-partners" },
  { to: "/staff/points", icon: Coins, key: "nav.points", roles: ["super_admin", "admin"], testId: "staff-nav-points" },
  { to: "/staff/system", icon: ShieldCheck, key: "nav.system", roles: ["super_admin"], testId: "staff-nav-system" },
];

export const StaffLayout = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  usePrivateSeo(t("nav.dashboard"));

  useEffect(() => {
    api.get("/dashboard/staff").then(({ data }) => setPending(data.stats?.needs_review || 0)).catch(() => {});
  }, []);

  const visible = links.filter((l) => l.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-border bg-card px-4">
        <button className="lg:hidden" data-testid="staff-menu-toggle" onClick={() => setOpen(!open)}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <div className="font-display text-lg font-bold tracking-tight">
          Vytali<span className="text-accent">Days</span>
          <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
            {t(`admin.${user?.role}`)}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="staff-notifications-link" onClick={() => navigate("/notifications")}
            className="relative h-10 w-10 rounded-full hover:bg-muted flex items-center justify-center">
            <Bell className="h-5 w-5" />
            {pending > 0 && (
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-accent" />
            )}
          </button>
          <LangSwitcher />
          <button data-testid="staff-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold hover:bg-muted">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">{t("common.logout")}</span>
          </button>
        </div>
      </header>
      <div className="flex">
        <aside className={`${open ? "block" : "hidden"} lg:block w-full lg:w-64 shrink-0 border-r border-border bg-card lg:min-h-[calc(100vh-4rem)] p-3`}>
          <nav className="space-y-1">
            {visible.map(({ to, icon: Icon, key, testId }) => (
              <NavLink key={to} to={to} end={to === "/staff"} data-testid={testId}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex h-12 items-center gap-3 rounded-xl px-4 text-sm font-semibold transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
                  }`}>
                <Icon className="h-4 w-4" />
                {t(key)}
                {key === "nav.review" && pending > 0 && (
                  <span className="ml-auto rounded-full bg-accent px-2 py-0.5 text-xs text-white">{pending}</span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className={`${open ? "hidden" : "block"} lg:block min-w-0 flex-1 p-4 sm:p-6 lg:p-8`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};
